import React from 'react';
import { debounce, noop } from 'lodash';
import { Spinner } from '@wix/wix-vod-shared/components';
import { prepareEmbedVideoCards } from '@wix/wix-vod-shared/widget';
import {
  guid,
  getBuyButtonDisplayTypeForPaidVideo,
  getTrailerTypeString,
  focusWithin,
  focusSource,
  parseCards,
  isLiveVideo,
  isPlayable,
  isVideoOverQuota,
  URL_STATE_PURCHASE,
  URL_STATE_SHARE,
  URL_STATE_SUBSCRIBE,
  URL_PARAM_STATE,
} from '@wix/wix-vod-shared/common';
import { appLoaded } from '../../services/fedops-logger';
import LiveStartHandler from '../../utils/live-start-handler';

import { logBi, logSharedBi, setDefaults } from '../../bi';

import Error from './error/error';
import DesktopPlayer from './desktop/desktop';
import MobilePlayer from './mobile/mobile';
import i18n from '../../i18n';

import {
  getEmbedVideo,
  getChannelData,
  getSiteUrl,
  getVideoUrls,
  getVideoCards,
} from '../../services/vod';
import {
  getQueryParameters,
  isMobile,
  fitImage,
  isChannelSubscriptionOnly,
} from '../../utils';

import './embed-video.scss';

import { PRODUCTION_HOSTS } from '@wix/wix-vod-constants/env';

const VISIBLE_ALWAYS = 'always';
const MAX_LOGO_WIDTH = 450;
const MAX_LOGO_HEIGHT = 36;
const VIDEO_CARD_THUMBNAIL_SIZE = 110;

export default class EmbedVideo extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      width: window.innerWidth,
      height: window.innerHeight,
      siteUrl: null,
      videoItem: null,
      vidiUrls: null,
      isLoading: true,
      isMobileMode: isMobile(),
      hasError: false,
      VODPlayer: null,
      correlationId: guid(),

      playerSettings: {
        areElementsRounded: false,
        canShowTitle: true,
        canShowFullInfo: true,
        canShareVideo: true,
        logo: false,
      },
    };

    this.onResizeDebounced = debounce(this.onResize.bind(this), 50);
    this.settingsModuleConfig = {
      fontFamily: 'Madefor',
      qualityEnabled: true,
      speedEnabled: true,
      texts: {
        normalSpeed: i18n.t('playerSettings.speed.normalSpeed'),
        menuSpeedTitle: i18n.t('playerSettings.speed.title'),
        buttonTooltip: i18n.t('playerSettings.tooltip'),
        menuQualityTitle: i18n.t('playerSettings.quality.title'),
        autoQualityTitle: i18n.t('playerSettings.quality.auto'),
      },
      onMenuClicked: this.onMenuClicked,
      onPopoverVisibilityChange: this.onPopoverVisibilityChange,
      onSpeedChange: this.onSpeedChange,
      onQualityChange: this.onQualityChange,
    };
  }

  onMenuClicked = (target) => {
    const { videoItem, channelData } = this.state;

    logBi('playerVideoSettingsMenuClick', {
      origin: 'embed',
      channelID: channelData?.id,
      videoID: videoItem?.itemId,
      target,
    });
  };

  onPopoverVisibilityChange = (visible) => {
    const { videoItem, channelData } = this.state;

    if (visible) {
      logBi('playerVideoSettingsMenuOpen', {
        origin: 'embed',
        channelID: channelData?.id,
        videoID: videoItem?.itemId,
      });
    }
  };

  onSpeedChange = (value) => {
    const { videoItem, channelData } = this.state;

    logBi('playerVideoSettingsChange', {
      origin: 'embed',
      channelID: channelData?.id,
      videoID: videoItem?.itemId,
      target: 'speed',
      value: String(value),
    });
  };

  onQualityChange = (value) => {
    const { videoItem, channelData } = this.state;

    logBi('playerVideoSettingsChange', {
      origin: 'embed',
      channelID: channelData?.id,
      videoID: videoItem?.itemId,
      target: 'quality',
      value,
    });
  };

  componentDidMount() {
    this.fetchData();

    this.focusWithinHandler = focusWithin();

    focusSource.engage(document.documentElement);

    window.addEventListener('resize', this.onResizeDebounced);
  }

  componentWillUnmount() {
    this.focusWithinHandler.disengage();

    focusSource.disengage(document.documentElement);

    if (this.liveStartHandler) {
      this.liveStartHandler.handleLiveUnmount();
    }

    window.removeEventListener('resize', this.onResizeDebounced);
  }

  sendEmbedVideoReadyBi(videoItem, isOverQuota) {
    logBi('widget.embed.opened', {
      videoWidth: window.innerWidth,
      videoHeight: window.innerHeight,
      buyTypeDisplayed: getBuyButtonDisplayTypeForPaidVideo(videoItem),
      trailerType: getTrailerTypeString(videoItem),
      loadingTime: window.performance
        ? Math.round(window.performance.now())
        : null,
      overQuoted: isOverQuota,
    });

    appLoaded();
  }

  goToSite = (param = '') => {
    const { siteUrl } = this.state;
    const queryParams = param ? `&${URL_PARAM_STATE}=${param}` : '';
    window.open(`${siteUrl}${queryParams}`, '_blank');
  };

  goToSiteWithPurchase = () => {
    const { channelData } = this.state;
    if (isChannelSubscriptionOnly(channelData)) {
      this.goToSite(URL_STATE_SUBSCRIBE);
    } else {
      this.goToSite(URL_STATE_PURCHASE);
    }
  };

  goToSiteWithShare = () => {
    this.goToSite(URL_STATE_SHARE);
  };

  onEnd = () => {
    const {
      isLive,
      siteUrl,
      correlationId,
      channelData: { id: channelID },
      videoItem: {
        id: videoID,
        mediaInfo: {
          fileInput: { duration },
        },
        videoSource: videoType,
      },
    } = this.state;

    logBi('widget.embed.vid.played', {
      correlationId,
      channelID,
      videoID,
      duration,
      isLive,
      videoType,
      siteUrl,
    });

    this.setState({ correlationId: guid() });
  };

  onResize() {
    this.setState({
      width: window.innerWidth,
      height: window.innerHeight,
    });
  }

  shouldShowWatchOnSiteAlways(logoVisibility) {
    return logoVisibility === VISIBLE_ALWAYS;
  }

  getLogoSettings({
    selectedLogoUrl,
    logoUrl,
    showLogo = true,
    logoVisibility,
  } = {}) {
    const url = selectedLogoUrl || logoUrl;
    const shouldShowLogo = Boolean(showLogo && url);
    return {
      src: shouldShowLogo
        ? fitImage(url, MAX_LOGO_WIDTH, MAX_LOGO_HEIGHT)
        : null,
      showAlways:
        shouldShowLogo && this.shouldShowWatchOnSiteAlways(logoVisibility),
    };
  }

  getPlayerSettings(embedOptions = {}, siteUrl) {
    const {
      showTitle = true,
      showInfo = true,
      showShare = true,
    } = embedOptions;
    return {
      ...this.state.playerSettings,
      canShowTitle: showTitle,
      canShowFullInfo: showInfo,
      canShareVideo: showShare,
      logo: this.getLogoSettings(embedOptions, siteUrl),
    };
  }

  fetchSiteUrl({ instanceId, pathToPage, videoId, compId }) {
    return getSiteUrl({ instanceId, pathToPage, videoId, compId }).then(
      (siteUrl) => {
        this.setState({ siteUrl });
        return siteUrl;
      },
    );
  }

  fetchVideoUrls(itemId, channelId, instanceId) {
    getVideoUrls({ videoId: itemId, channelId, instanceId }).then(
      (vidiUrls) => {
        this.setState({ vidiUrls });
      },
      () => {
        this.setState({ hasError: true });
      },
    );
  }

  fetchVideoCards({ channelId, videoId, instanceId }) {
    return getVideoCards({ channelId, videoId, instanceId })
      .then((rawVideoCards) => parseCards(rawVideoCards))
      .then((videoCards) =>
        prepareEmbedVideoCards({
          videoCards,
          imageHost: PRODUCTION_HOSTS.image,
          thumbnailSize: VIDEO_CARD_THUMBNAIL_SIZE,
        }),
      );
  }

  fetchData() {
    const {
      videoId,
      channelId,
      instanceId,
      pathToPage,
      compId,
      sitePageId,
      biToken,
    } = getQueryParameters();

    Promise.all([
      getEmbedVideo({ videoId, channelId, instanceId }),
      // We'r fetching channel info just to display proper CTA for subscription only channel
      // it should be reconsidered
      getChannelData({ channelId, instanceId }),
      this.fetchSiteUrl({ instanceId, pathToPage, videoId, compId }),
      this.fetchVideoCards({ channelId, videoId, instanceId }),
    ])
      .then(([videoItem, channelData, siteUrl, videoCards]) => {
        setDefaults({ videoID: videoItem.id });

        const isLive = isLiveVideo(videoItem);
        const isOverQuota = isVideoOverQuota(videoItem);

        this.sendEmbedVideoReadyBi(videoItem, isOverQuota);

        this.setState({
          correlationId: guid(),
          instanceInfo: {
            sitePageId,
            instanceId,
            componentId: compId,
            biToken,
          },
          isLoading: false,
          videoItem,
          channelData,
          videoCards,
          playerSettings: this.getPlayerSettings(
            videoItem.embedOptions,
            siteUrl,
          ),
          isOverQuota,
          isLive,
        });
        if (isPlayable(videoItem)) {
          this.fetchVideoUrls(videoItem.itemId, channelId, instanceId);
        }
        if (isLive) {
          this.liveStartHandler = new LiveStartHandler(
            videoItem,
            channelId,
            this.updateVideoItem,
            isMobile() ? noop : this.startAutoplay,
          );
          this.liveStartHandler.handleLive();
        }
      })
      .catch(() => {
        this.sendEmbedVideoReadyBi();
        this.setState({ hasError: true, isLoading: false });
      });
  }

  updateVideoItem = (videoItem) => {
    this.setState({
      videoItem,
    });
  };

  startAutoplay = () => {
    const { isLive } = this.state;

    logSharedBi('widget.video.autoplay.start', {
      videoType: isLive ? 'live' : 'upload',
    });
    this.setState({
      isVideoPlayRequested: true,
    });
  };

  getPosterUrl(videoItem) {
    return videoItem.customCoverUrl || videoItem.coverUrl;
  }

  render() {
    const {
      hasError,
      isLoading,
      siteUrl,
      isMobileMode,
      isOverQuota,
      isLive,
      correlationId,
      vidiUrls,
      videoItem,
      channelData,
      width,
      height,
      instanceInfo,
      videoCards,
      isVideoPlayRequested,
    } = this.state;

    if (hasError || isOverQuota) {
      return <Error dataHook="error" siteUrl={siteUrl} />;
    } else if (isLoading) {
      return <Spinner dataHook="spinner" />;
    } else if (isMobileMode) {
      return (
        <MobilePlayer
          dataHook="mobile-player"
          goToSite={this.goToSite}
          goToSiteWithPurchase={this.goToSiteWithPurchase}
          goToSiteWithShare={this.goToSiteWithShare}
          width={width}
          height={height}
          videoItem={videoItem}
          channelData={channelData}
          instanceInfo={instanceInfo}
          vidiUrls={vidiUrls}
          siteUrl={siteUrl}
          posterUrl={this.getPosterUrl(videoItem)}
          isLive={isLive}
          isOverQuota={isOverQuota}
          onEnd={this.onEnd}
          correlationId={correlationId}
          {...this.state.playerSettings}
        />
      );
    } else {
      return (
        <DesktopPlayer
          dataHook="desktop-player"
          goToSite={this.goToSite}
          goToSiteWithPurchase={this.goToSiteWithPurchase}
          goToSiteWithShare={this.goToSiteWithShare}
          width={width}
          height={height}
          instanceInfo={instanceInfo}
          videoItem={videoItem}
          channelData={channelData}
          vidiUrls={vidiUrls}
          siteUrl={siteUrl}
          posterUrl={this.getPosterUrl(videoItem)}
          videoCards={videoCards}
          isVideoPlayRequested={isVideoPlayRequested}
          isLive={isLive}
          isOverQuota={isOverQuota}
          onEnd={this.onEnd}
          correlationId={correlationId}
          showSettings
          settingsModuleConfig={this.settingsModuleConfig}
          {...this.state.playerSettings}
        />
      );
    }
  }
}
